/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description all accounts
 */

import _ from 'lodash';
import { FieldTypeConfigs } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { getNameMeta } from 'utils/claims/institutionalClaimRequestParser';
import { getQueryBuilderSchemaForAttributes } from 'utils/admin/customAttributes';
import { SEARCH_FIELDS_META } from 'app-constants/accounts';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { EMPTY_VALUE } from '@veritable/common/constants';
import { IFieldsData } from 'lib/SearchQueryBuilder/config/entity.config';

// filter data keys
const filterDataKeys = {
	accountNumber: 'accountNumber',
	accountAge: 'accountAge',
	guarantorName: 'guarantorName',
	patientName: 'patientName',
	patientIdentifier: 'patientIdentifier',
	accountReceivable: 'accountReceivable',
	pendingPatientResponsibility: 'pendingPR',
	billedAmount: 'billedAmount',
};

// get accounts filter schema
const getAccountsFilterSchema = ({
	forGuarantor,
	attributes,
}: {
	forGuarantor?: boolean;
	attributes?: CustomAttributes.IAllResponse[];
}): IFieldsData[] => {
	const customAttributesFilterSchema = _.compact(_.map(attributes, (attribute) => getQueryBuilderSchemaForAttributes(attribute)));
	return [
		...customAttributesFilterSchema,
		{
			...FieldTypeConfigs.number,
			field: filterDataKeys.accountAge,
			display_name: SEARCH_FIELDS_META.accountAge.label,
			custom_props: {
				placeholder: SEARCH_FIELDS_META.accountAge.placeholder,
				stringifiedValueRenderer: (value: number) => `${value} days`,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterDataKeys.accountNumber,
			isDefaultSelectedField: true,
			display_name: SEARCH_FIELDS_META.accountNumber.label,
			isSearchable: true,
			custom_props: {
				placeholder: SEARCH_FIELDS_META.accountNumber.placeholder,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterDataKeys.patientName,
			display_name: SEARCH_FIELDS_META.patientName.label,
			isSearchable: true,
			custom_props: {
				placeholder: SEARCH_FIELDS_META.patientName.placeholder,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterDataKeys.guarantorName,
			display_name: SEARCH_FIELDS_META.guarantorName.label,
			isSearchable: true,
			custom_props: {
				placeholder: SEARCH_FIELDS_META.guarantorName.placeholder,
			},
		},
		{
			...FieldTypeConfigs.text,
			field: filterDataKeys.patientIdentifier,
			isSearchable: true,
			display_name: SEARCH_FIELDS_META.patientIdentifier.label,
			custom_props: {
				placeholder: SEARCH_FIELDS_META.patientIdentifier.placeholder,
			},
		},
		{
			...FieldTypeConfigs.minMaxRange,
			field: filterDataKeys.accountReceivable,
			isSearchable: true,
			display_name: SEARCH_FIELDS_META.accountReceivable.label,
			custom_props: {
				prefix: '$',
				placeholder: SEARCH_FIELDS_META.accountReceivable.placeholder,
				stringifiedValueRenderer: (value: number) => `$${value}`,
			},
		},
		{
			...FieldTypeConfigs.minMaxRange,
			field: filterDataKeys.pendingPatientResponsibility,
			isSearchable: forGuarantor,
			display_name: SEARCH_FIELDS_META.pendingPatientResponsibility.label,
			custom_props: {
				placeholder: SEARCH_FIELDS_META.pendingPatientResponsibility.placeholder,
			},
		},
		{
			...FieldTypeConfigs.minMaxRange,
			field: filterDataKeys.billedAmount,
			display_name: SEARCH_FIELDS_META.billedAmount.label,
			custom_props: {
				prefix: '$',
				placeholder: SEARCH_FIELDS_META.billedAmount.placeholder,
				stringifiedValueRenderer: (value: number) => `$${value}`,
			},
		},
	];
};

// get accounts filter schema for guarantor
const getAccountsFilterSchemaForGuarantor = (): IFieldsData[] => {
	const fields = [
		filterDataKeys.accountNumber,
		filterDataKeys.patientName,
		filterDataKeys.accountAge,
		filterDataKeys.patientIdentifier,
		filterDataKeys.pendingPatientResponsibility,
	];
	return _.filter(getAccountsFilterSchema({ forGuarantor: true }), (field) => _.includes(fields, field.field));
};

// get name from fhir resource
const getNameFromFhirResource = (name: string | Fhir.HumanName[]): string => {
	if (_.isString(name)) {
		return name;
	}
	return getNameMeta(name)?.[0]?.fullName;
};

// format amount
const formatAmount = (amount: number): string | null => {
	if (!_.isNumber(amount)) {
		return EMPTY_VALUE;
	}

	const isNegative = amount < 0;
	const absoluteValue: number = Math.abs(amount);

	const formatted = absoluteValue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

	return isNegative ? `-$${formatted}` : `$${formatted}`;
};

// get account number
const getAccountNumber = (identifier: Fhir.Identifier[]): string | null => {
	return _.find(identifier, { system: fhirSystemMapping.accountNumber })?.value;
};

const getAmountBreakdown = (
	data: Accounts.IResponseModel,
): { billedAmount: number; payerPaid: number; payerAdjusted: number; collectedPR: number; writeoffs: number } => {
	return {
		billedAmount: data?.metadata?.billed,
		payerPaid: data?.metadata?.payerPaid,
		payerAdjusted: data?.metadata?.payerAdjusted,
		collectedPR: data?.metadata?.collectedPR,
		writeoffs: data?.metadata?.writeOff,
	};
};

// get patient info from fhir resource
const getPatientLinkInfo = (fhirResource: Fhir.Patient): { id: string; name: string } => {
	return {
		id: fhirResource?.id,
		name: getNameMeta(fhirResource?.name)?.[0]?.fullName,
	};
};

const getAmountReceivable = ({
	billedAmount,
	payerPaid,
	payerAdjusted,
	collectedPR,
	writeoffs,
}: {
	billedAmount: number;
	payerPaid: number;
	payerAdjusted: number;
	collectedPR: number;
	writeoffs: number;
}): number => {
	if (!_.isNumber(billedAmount)) {
		return null;
	}
	return billedAmount - (payerPaid || 0) - (payerAdjusted || 0) - (collectedPR || 0) - (writeoffs || 0);
};

// prepare write off data
const prepareWriteOffData = (claims: ClaimStatusRequests.IResponseModel[], reasonId: string): ClaimWriteOffItem[] => {
	return _.map(claims, (claim) => ({
		claimId: claim?.claimId,
		claimVersionId: claim?.fhirResource?.meta?.versionId,
		writeOffAmount: null,
		writeOffReasonId: reasonId,
		writeOffAllPending: true,
	}));
};

export {
	filterDataKeys,
	formatAmount,
	getAccountsFilterSchema,
	getNameFromFhirResource,
	getAccountNumber,
	getAmountBreakdown,
	getAccountsFilterSchemaForGuarantor,
	getAmountReceivable,
	getPatientLinkInfo,
	prepareWriteOffData,
};
